/**
 * Scope math (Page 2 of the New Job form).
 *
 * Money is handled exclusively in integer cents and tax rates in basis points,
 * so no calculation in this module ever touches binary floating-point money.
 * Quantities may be fractional (e.g. 1.5 hours); they are ordinary numbers and
 * are only ever multiplied into integer cents, with the result rounded once.
 *
 * Kept free of React and of jobForm UI helpers so invoice/PDF work can reuse
 * the exact same arithmetic later.
 */

/**
 * Default GST rate in basis points (1000 = 10%). Configured here rather than
 * hard-coded at call sites so a future settings surface can override it.
 */
export const DEFAULT_TAX_RATE_BPS = 1000;

/** Basis points per 100 percent — the divisor that turns bps into a fraction. */
const BPS_PER_UNIT = 10_000;

export interface CostingLineItemLike {
  quantity: number;
  unitAmountCents: number;
}

export interface CostSummaryCents {
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
}

/** One line item's cost: `quantity × unit amount`, rounded to whole cents. */
export function lineItemSubtotalCents(item: CostingLineItemLike): number {
  return Math.round((item.quantity ?? 0) * (item.unitAmountCents ?? 0));
}

/**
 * Sums every line item, then applies the tax rate: the tax is computed on the
 * rounded subtotal so the displayed total is always the sum of whole cents.
 */
export function costSummary(
  items: readonly CostingLineItemLike[],
  taxRateBps: number = DEFAULT_TAX_RATE_BPS,
): CostSummaryCents {
  const subtotalCents = items.reduce((sum, item) => sum + lineItemSubtotalCents(item), 0);
  const taxCents = Math.round((subtotalCents * (taxRateBps ?? 0)) / BPS_PER_UNIT);
  return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
}

/** Renders a basis-point rate as a plain percentage label, e.g. 1000 -> "10%". */
export function taxRatePercentLabel(taxRateBps: number): string {
  const percent = (taxRateBps ?? 0) / 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
}

/**
 * Parses a percentage input ("10", "12.5") into basis points, clamped to
 * 0–100%. Empty or unparseable input is 0 (GST-free) rather than an error,
 * matching the page's all-optional posture.
 */
export function parseTaxPercent(input: string): number {
  const cleaned = input.trim().replace(/[^0-9.]/g, "");
  if (!cleaned) return 0;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(10_000, Math.round(value * 100));
}

/** Renders basis points back into a percentage input string, e.g. 1000 -> "10". */
export function taxPercentToInput(taxRateBps: number): string {
  const percent = (taxRateBps ?? 0) / 100;
  if (!percent) return "";
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(2);
}

/**
 * Parses a quantity input ("2", "1.5") into a non-negative number. Empty or
 * unparseable input is 0 — the row simply contributes nothing until filled.
 */
export function parseQuantity(input: string): number {
  const cleaned = input.trim().replace(/[^0-9.]/g, "");
  if (!cleaned) return 0;
  const value = Number(cleaned);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** Renders a quantity back into an input string; unset/zero rows show empty. */
export function quantityToInput(quantity: number): string {
  return Number.isFinite(quantity) && quantity > 0 ? String(quantity) : "";
}
